import { datadogRum, RumActionEvent } from '@datadog/browser-rum'

import {
  baseConsumerUrl,
  basePartnerpageUrl,
  brandFromHost,
  companyUuid,
  ddRUM as ddRUMConfig,
} from 'embed/constants/config'
import { TrackingKey } from 'even-ts-static'

import { DEFAULT_SAMPLE_RATE, isSDataDogRUMampleRate, RUMContextKeys } from './constants'

const REDACTION_LABEL_ADDRESS = '[REDACTED=address]'

function charOccurrenceCount(str: string, charToCount: string): number {
  let charCount = 0

  for (const char of str) {
    if (char === charToCount) {
      charCount += 1
    }
  }

  return charCount
}

function containsPossibleStreetAddress(str: string): boolean {
  // Google Places street addresses will have at least 3 parts separated by commas
  return str && charOccurrenceCount(str, ',') > 2 ? true : false
}

datadogRum.init({
  beforeSend(event) {
    if (event.action) {
      const action = (event as RumActionEvent).action
      // Will be redacting street addresses present in an action name
      if (
        action.name === 'string' &&
        action.name.startsWith('click on') &&
        containsPossibleStreetAddress(action.name)
      ) {
        action.name = `click on ${REDACTION_LABEL_ADDRESS}`
      }
      // Will be redacting street addresses present in an action target name
      if (typeof action.target?.name === 'string' && containsPossibleStreetAddress(action.target.name)) {
        action.target.name = REDACTION_LABEL_ADDRESS
      }
    }
  },
  trackInteractions: true,
  trackSessionAcrossSubdomains: true,
  useCrossSiteSessionCookie: true,
  useSecureSessionCookie: true,
  ...ddRUMConfig,
  sampleRate: isSDataDogRUMampleRate(ddRUMConfig.sampleRate) ? ddRUMConfig.sampleRate : DEFAULT_SAMPLE_RATE,
})

if (baseConsumerUrl) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_BASE_URL_CONSUMER, baseConsumerUrl)
}

if (basePartnerpageUrl) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_BASE_URL_PARTNERPAGE, basePartnerpageUrl)
}

if (brandFromHost) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_BRAND_FROM_HOST, brandFromHost)
}

if (companyUuid) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_COMPANY_UUID, companyUuid)
}

export function removeRumGlobalContext(contextKey: string): void {
  const globalContext = datadogRum.getRumGlobalContext()
  if (contextKey in globalContext) {
    datadogRum.setRumGlobalContext(
      Object.entries(globalContext).reduce((newContext: typeof globalContext, [key, value]) => {
        if (key !== contextKey) {
          newContext[key] = value
        }
        return newContext
      }, {}),
    )
  }
}

export function sendValidationErrorOnBlur(actionData): void {
  datadogRum.addAction(TrackingKey.ValidationErrorOnBlur, actionData)
}

export { datadogRum }
